<template>
  <div
    class="relative"
    :class="{'opacity-50 cursor-not-allowed': disabled}"
  >
    <select
      ref="input"
      :class="selectClasses"
      :value="value"
      :disabled="disabled"
      v-on="listeners"
      @change="onChange"
    >
      <slot>
        <option
          v-if="placeholder"
          value=""
          class="text-black"
        >
          {{ placeholder }}
        </option>

        <template v-if="Array.isArray(options)">
          <option
            v-for="(name, index) in options"
            :key="index"
            :value="name"
          >
            {{ name }}
          </option>
        </template>

        <template v-else>
          <option
            v-for="(option, name) in options"
            :key="name"
            :value="name"
          >
            {{ option }}
          </option>
        </template>
      </slot>
    </select>

    <div class="absolute pin-t pin-r flex items-center justify-center h-full leading-none">
      <VSpinner
        v-if="loading"
        class="mx-3"
      />

      <VIcon
        v-else
        name="select-caret"
        class="px-2"
      />
    </div>

    <div
      v-if="iconName"
      class="absolute pin-t flex items-center h-full"
      :class="[
        $style['icon'],
        {
          'text-success-50': valid,
          'text-danger-50': invalid
        }
      ]"
    >
      <VIcon :name="iconName" />
    </div>
  </div>
</template>

<script>
import {
  VIcon,
  VSpinner
} from '@c/base'

export default {
  inheritAttrs: false,

  components: {
    VIcon,
    VSpinner
  },

  props: {
    value: {
      type: [String, Number],
      default: null
    },
    theme: {
      type: String,
      default: 'primary'
    },
    valid: {
      type: Boolean,
      default: false
    },
    invalid: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    options: {
      type: [Array, Object],
      default: () => ({})
    },
    optional: {
      type: Boolean,
      default: false
    },
    downIconClass: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      inputValue: this.value
    }
  },

  computed: {
    listeners () {
      const listeners = { ...this.$listeners }

      delete listeners.input
      delete listeners.blur
      delete listeners.focus

      return listeners
    },

    selectClasses () {
      return [
        ...THEMES[this.theme],
        ...DEFAULT_INPUT_CLASSES,
        {
          'border-success-80': this.valid,
          'border-danger-80': this.invalid
        }
      ]
    },

    iconName () {
      if (this.invalid) {
        return 'exclamation-circle'
      }

      return this.valid
        ? 'check'
        : null
    }
  },

  methods: {
    onChange (event) {
      this.inputValue = event.target ? event.target.value : event

      this.$emit('input', this.inputValue)
    }
  }
}

const DEFAULT_INPUT_CLASSES = [
  'relative',
  'w-full',
  'h-full',
  'appearance-none',
  'border',
  'border-t-0',
  'border-l-0',
  'border-r-0',
  'border-solid',
  'text-secondary-grey',
  'pl-1',
  'pr-12',
  'text-sm',
  'outline-none',
  'z-10',
  'rounded-none',
  'bg-transparent'
]

const THEMES = {
  primary: [
    'text-black',
    'border-black',
    'focus:border',
    'focus:border-t-0',
    'focus:border-l-0',
    'focus:border-r-0',
    'focus:border-black',
    'bg-transparent'
  ],
  white: [
    'text-white',
    'border-white',
    'bg-transparent'
  ],
  danger: [
    'text-black',
    'border-black',
    'focus:border',
    'focus:border-t-0',
    'focus:border-l-0',
    'focus:border-r-0',
    'focus:border-danger-50',
    'bg-transparent'
  ]
}
</script>

<style module>
.icon {
  right: -30px;
}
</style>
